import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const BirthdayListView = ({ 
  birthdays = [], 
  currentDate,
  onSendWish, 
  onViewContact,
  onScheduleMessage,
  className = '' 
}) => {
  const today = new Date();
  
  const calculateDaysUntilBirthday = (dateOfBirth) => {
    const birth = new Date(dateOfBirth);
    const currentYear = today?.getFullYear();
    
    // Set birthday to current year
    const thisYearBirthday = new Date(currentYear, birth.getMonth(), birth.getDate());
    
    // If birthday has passed this year, calculate for next year
    if (thisYearBirthday < today) {
      thisYearBirthday?.setFullYear(currentYear + 1);
    }
    
    const timeDiff = thisYearBirthday?.getTime() - today?.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age--;
    }
    
    return age + 1; // Age they will turn
  };

  const formatBirthdayDate = (dateOfBirth) => {
    const birth = new Date(dateOfBirth);
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    return `${monthNames?.[birth?.getMonth()]} ${birth?.getDate()}`;
  };

  const getStatusColor = (contact) => {
    const daysUntil = calculateDaysUntilBirthday(contact?.dateOfBirth);
    
    if (daysUntil === 0) return 'text-primary';
    if (daysUntil <= 7) return 'text-warning';
    return 'text-text-secondary';
  };

  const getStatusText = (contact) => {
    const daysUntil = calculateDaysUntilBirthday(contact?.dateOfBirth);
    
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil <= 7) return `In ${daysUntil} days`;
    if (daysUntil <= 30) return `In ${daysUntil} days`;
    return `In ${Math.ceil(daysUntil / 30)} months`;
  };

  // Filter and sort birthdays for current month
  const currentMonthBirthdays = birthdays?.filter(contact => {
      const birth = new Date(contact.dateOfBirth);
      return birth?.getMonth() === currentDate?.getMonth();
    })?.sort((a, b) => {
      const birthA = new Date(a.dateOfBirth);
      const birthB = new Date(b.dateOfBirth);
      return birthA?.getDate() - birthB?.getDate();
    });

  if (currentMonthBirthdays?.length === 0) {
    return (
      <div className={`bg-surface rounded-lg border border-border shadow-card p-8 text-center ${className}`}>
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
        <h3 className="text-lg font-medium text-text-primary mb-2">
          No Birthdays This Month
        </h3>
        <p className="text-text-secondary">
          There are no birthdays scheduled for {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-surface rounded-lg border border-border shadow-card ${className}`}>
      <div className="p-4 lg:p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">
          Birthdays in {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} ({currentMonthBirthdays?.length})
        </h3>
      </div>
      <div className="divide-y divide-border">
        {currentMonthBirthdays?.map((contact) => {
          const daysUntil = calculateDaysUntilBirthday(contact?.dateOfBirth);
          let age = calculateAge(contact?.dateOfBirth);
          const isToday = daysUntil === 0;

          return (
            <div
              key={contact?.id}
              className={`p-4 lg:p-6 hover:bg-muted/50 transition-micro ${
                isToday ? 'bg-primary/5 border-l-4 border-l-primary' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden bg-muted">
                      {contact?.avatar ? (
                        <Image
                          src={contact?.avatar}
                          alt={contact?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="User" size={24} className="text-text-secondary" />
                        </div>
                      )}
                    </div>
                    {isToday && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="Gift" size={12} color="white" />
                      </div>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-text-primary truncate">
                        {contact?.name}
                      </h4>
                      {isToday && (
                        <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                          Today
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                      <span>{formatBirthdayDate(contact?.dateOfBirth)}</span>
                      <span>Turning {age}</span>
                      <span className={getStatusColor(contact)}>
                        {getStatusText(contact)}
                      </span>
                    </div>

                    {contact?.notes && (
                      <p className="text-sm text-text-secondary mt-1 truncate">
                        {contact?.notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {isToday ? (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onSendWish(contact)}
                      iconName="Send"
                      iconPosition="left"
                    >
                      Send Wish
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onScheduleMessage(contact)}
                      iconName="Clock"
                      iconPosition="left"
                    >
                      Schedule
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewContact(contact)}
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BirthdayListView;